import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faCompress,
  faExpand,
  faEye,
  faEyeSlash,
  faImages,
  faPause,
  faPlay,
} from "@fortawesome/free-solid-svg-icons";

import { useGetTourDetail } from "../../../api/tours";
import PanoramaEditorViewer from "../../../Pages/Tours/components/PanoramaEditorViewer";
import {
  hydrateTourFromApi,
  openHotspotUrl,
  toPersistedId,
} from "../../../Pages/Tours/utils/tourEditorUtils";
import TourLoadOverlay from "./TourLoadOverlay";
import styles from "./TourViewer.module.css";

const MIN_LOADER_MS = 420;
// Change this value to adjust the automatic panorama rotation speed.
const AUTO_ROTATE_SPEED_DEGREES_PER_SECOND = 3;
const AUTO_ROTATE_SPEED =
  (AUTO_ROTATE_SPEED_DEGREES_PER_SECOND * Math.PI) / 180;

const relatedId = value => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return toPersistedId(value.id);
  return toPersistedId(value);
};

export const is360TourScene = scene =>
  scene?.type === "360Tour" || Boolean(relatedId(scene?.tour_360));

export const getTourIdFromScene = scene => relatedId(scene?.tour_360);

export default function TourViewer({ tourId, initialTour }) {
  const navigate = useNavigate();
  const nestedHasGraph = Array.isArray(initialTour?.scenes);
  const resolvedTourId = toPersistedId(tourId) || relatedId(initialTour);
  const {
    data: tourData,
    isLoading,
    isError,
  } = useGetTourDetail(nestedHasGraph ? null : resolvedTourId);

  const tour = useMemo(() => {
    const raw = nestedHasGraph ? initialTour : tourData;
    return raw ? hydrateTourFromApi(raw) : null;
  }, [nestedHasGraph, initialTour, tourData]);

  const [activeSceneId, setActiveSceneId] = useState(null);
  const [transitionKey, setTransitionKey] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [thumbTooltip, setThumbTooltip] = useState(null);
  const [isThumbBarOpen, setIsThumbBarOpen] = useState(false);
  const [isChromeDimmed, setIsChromeDimmed] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAutoRotating, setIsAutoRotating] = useState(false);
  const initialLoadDoneRef = useRef(false);
  const loadStartedAtRef = useRef(Date.now());
  const hideLoaderTimerRef = useRef(null);
  const viewerRef = useRef(null);
  const tourViewerRef = useRef(null);

  useEffect(() => {
    if (!tour) return;
    setActiveSceneId(tour.startSceneId ?? tour.scenes[0]?.id ?? null);
    setTransitionKey(key => key + 1);
  }, [tour?.id]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === tourViewerRef.current);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      if (hideLoaderTimerRef.current) clearTimeout(hideLoaderTimerRef.current);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const resolvedActiveId =
    activeSceneId ?? tour?.startSceneId ?? tour?.scenes?.[0]?.id ?? null;

  const activeScene = useMemo(
    () => tour?.scenes?.find(scene => scene.id === resolvedActiveId) ?? null,
    [tour, resolvedActiveId]
  );

  const changeScene = sceneId => {
    if (sceneId == null) return;
    if (sceneId !== resolvedActiveId) {
      setActiveSceneId(sceneId);
    }
    setTransitionKey(key => key + 1);
  };

  const handleHotspotSceneChange = sceneId => {
    viewerRef.current?.preserveLook?.();
    changeScene(sceneId);
  };

  const handleThumbSelect = sceneId => {
    if (sceneId == null) return;
    if (sceneId === resolvedActiveId) {
      viewerRef.current?.resetCamera?.({ immediate: true });
      return;
    }
    viewerRef.current?.requestDefaultLook?.();
    changeScene(sceneId);
  };

  const hideThumbTooltip = () => setThumbTooltip(null);

  const toggleThumbBar = () => {
    hideThumbTooltip();
    setIsThumbBarOpen(open => !open);
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await tourViewerRef.current?.requestFullscreen?.();
    } catch (error) {
      console.warn("[360 Tour] Fullscreen is unavailable:", error);
    }
  };

  const showThumbTooltip = (event, name) => {
    if (!name) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setThumbTooltip({
      name,
      left: rect.left + rect.width / 2,
      top: rect.top,
    });
  };

  const handleThumbIntent = scene => {
    if (!scene?.imageUrl || scene.id === resolvedActiveId) return;
    viewerRef.current?.prefetchIntent?.(scene.imageUrl);
  };

  const handleLoadingChange = useCallback(loading => {
    if (initialLoadDoneRef.current) return;
    if (hideLoaderTimerRef.current) {
      clearTimeout(hideLoaderTimerRef.current);
      hideLoaderTimerRef.current = null;
    }
    if (loading) {
      loadStartedAtRef.current = Date.now();
      setIsInitialLoading(true);
      return;
    }
    const elapsed = Date.now() - loadStartedAtRef.current;
    const wait = Math.max(0, MIN_LOADER_MS - elapsed);
    hideLoaderTimerRef.current = setTimeout(() => {
      initialLoadDoneRef.current = true;
      setIsInitialLoading(false);
    }, wait);
  }, []);

  const fetchingTour = !nestedHasGraph && isLoading;
  const showLoader = fetchingTour || isInitialLoading || (!isError && !tour);

  if (!nestedHasGraph && !resolvedTourId) {
    return (
      <div className={styles.tourViewer}>
        <div className={styles.status}>This 360 tour could not be loaded.</div>
      </div>
    );
  }

  if (isError && !tour) {
    return (
      <div className={styles.tourViewer}>
        <div className={styles.status}>This 360 tour could not be loaded.</div>
      </div>
    );
  }

  if (!fetchingTour && tour && !tour.scenes?.length) {
    return (
      <div className={styles.tourViewer}>
        <div className={styles.status}>No panorama scenes in this tour.</div>
      </div>
    );
  }

  return (
    <div
      ref={tourViewerRef}
      className={`${styles.tourViewer} ${isThumbBarOpen ? styles.sceneTrayOpen : ""}`}
    >
      <div className={styles.canvasArea}>
        {activeScene?.imageUrl || activeScene?.videoUrl ? (
          <PanoramaEditorViewer
            ref={viewerRef}
            scene={activeScene}
            transitionKey={transitionKey}
            editorMode={false}
            autoRotate={isAutoRotating}
            autoRotateSpeed={AUTO_ROTATE_SPEED}
            onSceneChange={handleHotspotSceneChange}
            onHotspotClick={openHotspotUrl}
            onLoadingChange={handleLoadingChange}
            tourScenes={tour?.scenes}
          />
        ) : (
          !showLoader && (
            <div className={styles.status}>No panorama scenes in this tour.</div>
          )
        )}
        <TourLoadOverlay
          visible={showLoader}
          imageUrl={activeScene?.imageUrl}
          label={
            activeScene?.name
              ? `Loading ${activeScene.name}`
              : "Loading 360 tour"
          }
        />
      </div>
      <header
        className={`${styles.topBar} ${isChromeDimmed ? styles.topBarDimmed : ""}`}
      >
        <button
          type="button"
          className={styles.brandButton}
          onClick={() => navigate("/")}
          aria-label="Go to home page"
        >
          <span className={styles.brandFallback}>WebTwin</span>
        </button>
        <div className={styles.topActions}>
          <button
            type="button"
            className={`${styles.topAction} ${isThumbBarOpen ? styles.topActionActive : ""}`}
            onClick={toggleThumbBar}
            aria-pressed={isThumbBarOpen}
          >
            <FontAwesomeIcon icon={faImages} />
            <span>Views</span>
          </button>
          <button type="button" className={styles.topAction} onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </button>
        </div>
      </header>
      {tour?.scenes?.length > 0 && (
        <div
          className={`${styles.bottomBar} ${
            isThumbBarOpen ? styles.bottomBarOpen : styles.bottomBarCollapsed
          }`}
        >
          <button
            type="button"
            className={styles.thumbBarToggle}
            onClick={toggleThumbBar}
            aria-expanded={isThumbBarOpen}
            aria-label={isThumbBarOpen ? "Hide scene bar" : "Show scene bar"}
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              className={styles.thumbBarToggleIcon}
            />
          </button>
          <div className={styles.thumbStrip}>
            <nav
              className={styles.thumbBar}
              aria-label="Tour scenes"
              onScroll={hideThumbTooltip}
            >
              {tour.scenes.map(scene => (
                <button
                  key={scene.id}
                  type="button"
                  className={`${styles.thumb} ${
                    scene.id === resolvedActiveId ? styles.thumbActive : ""
                  }`}
                  onClick={() => handleThumbSelect(scene.id)}
                  onMouseEnter={event => {
                    showThumbTooltip(event, scene.name);
                    handleThumbIntent(scene);
                  }}
                  onMouseLeave={hideThumbTooltip}
                  onFocus={event => {
                    showThumbTooltip(event, scene.name);
                    handleThumbIntent(scene);
                  }}
                  onBlur={hideThumbTooltip}
                  aria-label={scene.name}
                  aria-current={scene.id === resolvedActiveId ? "true" : undefined}
                  tabIndex={isThumbBarOpen ? 0 : -1}
                >
                  <img
                    src={scene.imageUrl}
                    alt=""
                    width={148}
                    height={84}
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
      <div className={styles.viewerActions} aria-label="Viewer controls">
        <button
          type="button"
          className={`${styles.viewerAction} ${
            isAutoRotating ? styles.viewerActionActive : ""
          }`}
          onClick={() => setIsAutoRotating(rotating => !rotating)}
          aria-label={isAutoRotating ? "Pause automatic rotation" : "Play automatic rotation"}
          aria-pressed={isAutoRotating}
          title={isAutoRotating ? "Pause rotation" : "Play rotation"}
        >
          <FontAwesomeIcon icon={isAutoRotating ? faPause : faPlay} />
        </button>
        <button
          type="button"
          className={`${styles.viewerAction} ${styles.fullscreenAction}`}
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? "Exit full screen" : "Enter full screen"}
          title={isFullscreen ? "Exit full screen" : "Full screen"}
        >
          <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
        </button>
        <button
          type="button"
          className={`${styles.viewerAction} ${isChromeDimmed ? styles.viewerActionActive : ""}`}
          onClick={() => setIsChromeDimmed(dimmed => !dimmed)}
          aria-label={isChromeDimmed ? "Restore navigation" : "Dim navigation"}
          aria-pressed={isChromeDimmed}
          title={isChromeDimmed ? "Restore navigation" : "Dim navigation"}
        >
          <FontAwesomeIcon icon={isChromeDimmed ? faEyeSlash : faEye} />
        </button>
      </div>
      {thumbTooltip ? (
        <span
          className={styles.thumbTooltip}
          role="tooltip"
          style={{ left: thumbTooltip.left, top: thumbTooltip.top }}
        >
          {thumbTooltip.name}
        </span>
      ) : null}
    </div>
  );
}
